import {
  copyMarkdownNativeSectionWithNoteSettings,
  copySectionWithNoteSettings,
  copySectionWithSettings,
  sectionCopySettingsAtom,
  type SectionCopyRoute,
} from '@/components/tiptap/shared/copy-section';
import { copiedSectionAtom } from '@/components/tiptap/shared/section-atoms';
import { useCopySettings } from '@/features/copySettings';
import {
  isSupersededCopyError,
  reportCopyFailure,
} from '@/helpers/copyHelpers/reportCopyFailure';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type NoteEditorType } from '@/models/analytics/features/notes';
import { logger } from '@/utils/logger/logger';
import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { type Node } from '@tiptap/pm/model';
import { type Editor } from '@tiptap/react';
import { useAtom, useAtomValue } from 'jotai';
import { Copy, CopyCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

const NOTE_SETTINGS_COPIERS = {
  'markdown-native': copyMarkdownNativeSectionWithNoteSettings,
  vmarkdown: copySectionWithNoteSettings,
} as const;

/**
 * Section-copy routes carry their own vocabulary; failures are reported in
 * `session_copied`'s so a section failure and a note failure land on one
 * `editor_type` facet.
 */
const FAILURE_EDITOR_TYPE: Record<SectionCopyRoute, NoteEditorType> = {
  'markdown-native': 'markdown_native',
  vmarkdown: 'block_markdown',
};

const COPIED_INDICATOR_DURATION_MS = 1500;
const POINTER_LEAVE_RESET_DELAY_MS = 200;

export const SectionCopyButton = ({
  sectionNode,
  sectionId,
  editor,
  className,
  route,
}: {
  sectionNode: Node;
  sectionId: string;
  editor: Editor;
  className?: string;
  /**
   * Which twin is mounting this button. Required rather than inferred: the two
   * serialize through different pipelines, so a new mount site should be a
   * compile error, not a silent mis-route.
   */
  route: SectionCopyRoute;
}) => {
  const intl = useIntl();
  const [forceOpen, setForceOpen] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [copiedSection, setCopiedSection] = useAtom(copiedSectionAtom);
  const settings = useAtomValue(sectionCopySettingsAtom);
  const { settings: noteSettings, isImprovedCopyEnabled } = useCopySettings();
  const copied = copiedSection === sectionId;

  const scheduleReset = useCallback(
    (delayMs: number) => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        setCopiedSection(null);
      }, delayMs);
    },
    [setCopiedSection]
  );

  useEffect(
    () => () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    []
  );

  // Auto-reset the success state while the button is in "copied" mode,
  // regardless of whether the atom was set by this button or from elsewhere
  // (e.g. the copy-settings dropdown, which unmounts on close and cannot
  // safely own its own timer).
  //
  // When ownership transfers to another section (`copied` flips false), cancel
  // any pending timer so this button's stale reset cannot clear the newly-
  // copied section's state.
  useEffect(() => {
    if (!copied) {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
      return;
    }
    scheduleReset(COPIED_INDICATOR_DURATION_MS);
  }, [copied, scheduleReset]);

  const handleCopy = useCallback(async () => {
    try {
      // When the improved-copy flag is on, section copy reuses the user's
      // note-level four-toggle preference instead of the legacy per-device
      // `{ includeHeading, format }` settings.
      await (isImprovedCopyEnabled
        ? NOTE_SETTINGS_COPIERS[route](sectionNode, editor, noteSettings)
        : copySectionWithSettings(sectionNode, editor, settings));
      setForceOpen(true);
      setCopiedSection(sectionId);
      // Reuses the existing `note_copied` stream (rides Amplitude alongside
      // `ask_ai_copy`) — `copy_action` distinguishes the per-section copy.
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.NoteCopied,
        props: { copy_action: 'paste_note_section' },
      });
    } catch (error) {
      if (isSupersededCopyError(error)) return;
      // Reported before the log so an unguarded reporter would be visible as a
      // missing log line rather than passing silently.
      reportCopyFailure(error, {
        surface: 'section',
        editorType: FAILURE_EDITOR_TYPE[route],
      });
      logger.error('Error copying section content:', error);
    }
  }, [
    sectionNode,
    editor,
    settings,
    noteSettings,
    isImprovedCopyEnabled,
    route,
    sectionId,
    setCopiedSection,
  ]);

  return (
    <Tooltip open={forceOpen || undefined}>
      <TooltipTrigger asChild>
        <Button
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => void handleCopy()}
          onPointerLeave={() => {
            setForceOpen(false);
            scheduleReset(POINTER_LEAVE_RESET_DELAY_MS);
          }}
          className={cn(
            'absolute end-2 top-2 opacity-0 transition-opacity group-hover:opacity-100',
            className
          )}
          size="xs"
          variant="ghost"
          iconOnly
          data-testid="section-copy-button"
          aria-label={intl.formatMessage({
            defaultMessage: 'Copy section content',
            id: 'xAe73Rh/iX',
          })}
          type="button"
        >
          <CopyCheck
            className={cn(
              'absolute text-validation-success transition-opacity duration-150 ease-in-out',
              copied ? 'opacity-100' : 'opacity-0'
            )}
          />
          <Copy
            className={cn(
              'transition-opacity duration-150 ease-in-out',
              copied ? 'opacity-0' : 'opacity-100'
            )}
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent
        className={copied ? 'bg-validation-success text-text-primary-invert' : undefined}
      >
        <TooltipArrow className={copied ? 'fill-validation-success' : undefined} />
        {copied
          ? intl.formatMessage({ defaultMessage: 'Copied', id: 'p556q3uvbn' })
          : intl.formatMessage({
              defaultMessage: 'Copy section content',
              id: 'xAe73Rh/iX',
            })}
      </TooltipContent>
    </Tooltip>
  );
};
